
import config from './office365-config';
import axios from 'axios';

export default class AuthService {
    constructor() {
        this.ssoConfig = config.sso;
    }

    getAppsToken() {
        const match = document.cookie.match(new RegExp(`(^| )${this.ssoConfig.cookieName}=([^;]+)`));
        return match ? match[2] : null;
    }

    decodeToken(token) {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            return JSON.parse(decodeURIComponent(window.atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')));
        } catch (e) { return null; }
    }

    isExternalUser() {
        const token = this.getAppsToken();
        if (!token) return true;
        const decoded = this.decodeToken(token);
        if (!decoded) return true;
        const appName = this.ssoConfig.appName;
        const isDomainUser = decoded.apps_roles?.[appName]?.roles && Array.isArray(decoded.apps_roles[appName].roles);
        return !isDomainUser;
    }

    validateAppsToken() {
        const token = this.getAppsToken();
        if (!token) return 'missing';
        const decoded = this.decodeToken(token);
        if (!decoded) return 'invalid';
        if (decoded.exp && Date.now() >= decoded.exp * 1000) return 'expired';

        const appName = this.ssoConfig.appName;
        if (decoded.apps_roles?.[appName]?.roles && Array.isArray(decoded.apps_roles[appName].roles)) return 'valid';

        if (decoded.roleType === 'distributor' || decoded.roleType === 'admin') return 'valid';

        return 'no_permission';
    }

    getUser() {
        const token = this.getAppsToken();
        if (!token) return { userName: '', displayName: '', email: '' };
        const decoded = this.decodeToken(token);
        return {
            userName: decoded.preferred_username || decoded.email || decoded.name || '',
            displayName: decoded.name || decoded.preferred_username || '',
            email: decoded.email || decoded.preferred_username || ''
        };
    }

    logout() {
        const { hostname } = window.location;
        const isExternal = this.isExternalUser();
        const domain = '.salicru.com';
        document.cookie = `${this.ssoConfig.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;
        document.cookie = `${this.ssoConfig.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
        document.cookie = `${this.ssoConfig.cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        const currentUrl = encodeURIComponent(window.location.href);
        const loginUrl = (hostname.includes('products') && isExternal && this.ssoConfig.legacyAuthUrl)
            ? this.ssoConfig.legacyAuthUrl
            : this.ssoConfig.loginUrl;
        window.location.replace(`${loginUrl}?returnUrl=${currentUrl}`);
    }

    getToken() {
        const token = this.getAppsToken();
        if (!token) return Promise.reject("No apps-token found");

        const isProductsDomain = window.location.hostname.includes('products');

        if (isProductsDomain || this.isExternalUser()) {
            return axios.get('/products-homepage/api/sharepoint-token').then(response => {
                return response.data.token;
            }).catch(innerError => {
                console.error('[Auth] Local sharepoint-token endpoint failed:', innerError);
                const status = innerError.response && innerError.response.status;
                if (status === 401 || status === 403) this.logout();
                throw innerError;
            });
        }

        return axios.get(`${this.ssoConfig.portalAuthUrl}/auth/sharepoint`, {
            headers: { 'Authorization': `Bearer ${token}` }
        }).then(response => {
            const res = response.data;
            return (typeof res === 'string') ? res : (res.token || res.accessToken || res.access_token);
        }).catch(error => {
            if (!error.response || (error.response && (error.response.status === 401 || error.response.status === 403))) {
                return axios.get('/products-homepage/api/sharepoint-token').then(response => {
                    return response.data.token;
                }).catch(innerError => {
                    console.error('[Auth] Local sharepoint-token endpoint failed:', innerError);
                    const status = innerError.response && innerError.response.status;
                    if (status === 401 || status === 403) this.logout();
                    throw innerError;
                });
            }
            throw error;
        });
    }

    refreshToken() {
        const token = this.getAppsToken();

        const isProductsDomain = window.location.hostname.includes('products');
        if (isProductsDomain || this.isExternalUser()) {
            return axios.get('/products-homepage/api/sharepoint-token').then(response => {
                return response.data.token;
            });
        }

        return axios.get(`${this.ssoConfig.portalAuthUrl}/auth/sharepoint/refresh`, {
            headers: { 'Authorization': `Bearer ${token}` }
        }).then(response => {
            const res = response.data;
            return (typeof res === 'string') ? res : (res.token || res.accessToken || res.access_token);
        }).catch(error => {
            if (!error.response || (error.response && (error.response.status === 401 || error.response.status === 403))) {
                return axios.get('/products-homepage/api/sharepoint-token').then(response => {
                    return response.data.token;
                });
            }
            throw error;
        });
    }
}
